import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"

// The 3D view of a drone flight (pmfit K8-35, phase 3, 2026-10-28): the
// ground built from the heights the stitch saved (a PNG of at most 1,024 px
// a side, 16 bits a cell) with the flight's own picture laid over it, in a
// full-screen window the Drone Flights card opens. Drag to spin and tilt,
// scroll to zoom, right-drag (or two fingers) to move. The heights can be
// stretched (×1 to ×3) so a flat site still shows its shape, and the picture
// swapped for the coloured heights. Nothing loads until the window opens;
// everything is let go when it closes.
const MESH_MAX_SIDE = 512     // vertices along the longer side
const TEXTURE_MAX_SIDE = 4096 // the picture, scaled down for the GPU
const TILT_MIN = 0.05         // from straight above...
const TILT_MAX = 1.45         // ...to nearly level
const FT = 3.28084

export default class extends Controller {
  static targets = ["title", "note", "canvas", "stage", "readout", "stretch", "stretchValue", "photoButton", "heightsButton"]
  static values = { text: Object }

  disconnect() {
    this._dispose()
    this.resizeWatch?.disconnect()
    this.renderer?.dispose()
    this.renderer = null
  }

  // Everything the card knows: the heights PNG, the picture, the coloured
  // heights, the height range (metres above sea level) and the ground's size.
  async open({ title, heightsUrl, textureUrl, reliefUrl, lo, hi, widthM, heightM }) {
    this._dispose()
    const session = (this.session = (this.session || 0) + 1)
    this.element.classList.remove("hidden")
    this.titleTarget.textContent = title
    this.readoutTarget.classList.add("hidden")
    this._note(this.textValue.loading)
    this.lo = lo
    this.hi = hi
    this.widthM = widthM
    this.heightM = heightM
    this.urls = { photo: textureUrl, heights: reliefUrl }
    this._pressed("photo")
    try {
      const [grid, photo] = await Promise.all([this._loadHeights(heightsUrl), this._loadTexture(textureUrl)])
      if (session !== this.session) return
      this.grid = grid
      this.textures = { photo }
      this._build()
      this._note(null)
    } catch (error) {
      if (session !== this.session) return
      this._note(this.textValue.failed)
      console.error(error)
    }
  }

  close() {
    this.element.classList.add("hidden")
    this.session = (this.session || 0) + 1
    this._dispose()
  }

  stop(event) {
    event.stopPropagation()
  }

  // ── Loading ──────────────────────────────────────────────────────────

  // The PNG's red and green bytes make a 16-bit height from lo to hi; no
  // alpha is no ground. Every k-th cell is read so the mesh stays light.
  async _loadHeights(url) {
    const image = await this._image(url)
    const k = Math.max(1, Math.ceil(Math.max(image.width, image.height) / MESH_MAX_SIDE))
    const cols = Math.floor((image.width - 1) / k) + 1
    const rows = Math.floor((image.height - 1) / k) + 1
    const canvas = document.createElement("canvas")
    canvas.width = image.width
    canvas.height = image.height
    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    ctx.drawImage(image, 0, 0)
    const data = ctx.getImageData(0, 0, image.width, image.height).data
    const z = new Float32Array(cols * rows)
    const ground = new Uint8Array(cols * rows)
    const span = this.hi - this.lo
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = (r * k * image.width + c * k) * 4
        const on = data[i + 3] > 127
        ground[r * cols + c] = on ? 1 : 0
        z[r * cols + c] = on ? ((data[i] * 256 + data[i + 1]) / 65535) * span : 0
      }
    }
    // Outside the ground (and in any hole) each cell takes the nearest ground
    // height in its row, so the footprint's edge doesn't drop like a cliff; the
    // picture's see-through edge hides those cells anyway.
    for (let r = 0; r < rows; r++) {
      let last = null
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c
        if (ground[i]) last = z[i]
        else if (last !== null) z[i] = last
      }
      last = null
      for (let c = cols - 1; c >= 0; c--) {
        const i = r * cols + c
        if (ground[i]) last = z[i]
        else if (last !== null) z[i] = last
      }
    }
    return { cols, rows, z, ground }
  }

  // The picture, no bigger than the GPU likes.
  async _loadTexture(url) {
    const image = await this._image(url)
    const scale = Math.min(1, TEXTURE_MAX_SIDE / Math.max(image.width, image.height))
    let source = image
    if (scale < 1) {
      const canvas = document.createElement("canvas")
      canvas.width = Math.round(image.width * scale)
      canvas.height = Math.round(image.height * scale)
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height)
      source = canvas
    }
    const texture = new THREE.Texture(source)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.needsUpdate = true
    return texture
  }

  _image(url) {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.crossOrigin = "anonymous"
      image.onload = () => resolve(image)
      image.onerror = () => reject(new Error(`could not load ${url.split("?")[0]}`))
      image.src = url
    })
  }

  // ── The scene ────────────────────────────────────────────────────────

  _build() {
    const { cols, rows } = this.grid
    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvasTarget, antialias: true })
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      this.renderer.setClearColor(0xe5e7eb)
      this.resizeWatch = new ResizeObserver(() => this._resize())
      this.resizeWatch.observe(this.stageTarget)
      this._listen()
    }
    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.5, 100000)
    this.camera.up.set(0, 0, 1)
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.6))
    const sun = new THREE.DirectionalLight(0xffffff, 0.55) // from the north-west, high; soft, so the picture's own shadows show
    sun.position.set(-this.widthM, this.heightM, Math.max(this.widthM, this.heightM) * 0.9)
    this.scene.add(sun)

    this.geometry = new THREE.PlaneGeometry(this.widthM, this.heightM, cols - 1, rows - 1)
    this.material = new THREE.MeshLambertMaterial({ map: this._sharp(this.textures.photo), transparent: true, alphaTest: 0.3, side: THREE.DoubleSide })
    this.mesh = new THREE.Mesh(this.geometry, this.material)
    this.scene.add(this.mesh)
    this._raise()

    const size = Math.max(this.widthM, this.heightM)
    this.view = { theta: 0, phi: 0.95, radius: size * 1.15, target: new THREE.Vector3(0, 0, this._meanZ()) }
    this._resize()
    this._render()
  }

  _sharp(texture) {
    if (this.renderer) texture.anisotropy = Math.min(4, this.renderer.capabilities.getMaxAnisotropy())
    return texture
  }

  // Every vertex to its height (times the stretch); the ground's lowest point sits at 0.
  _raise() {
    const stretch = this._stretch()
    const pos = this.geometry.attributes.position
    const { z } = this.grid
    for (let i = 0; i < pos.count; i++) pos.setZ(i, z[i] * stretch)
    pos.needsUpdate = true
    this.geometry.computeVertexNormals()
  }

  _meanZ() {
    const { z, ground } = this.grid
    let sum = 0
    let n = 0
    for (let i = 0; i < z.length; i++) if (ground[i]) { sum += z[i]; n++ }
    return n ? (sum / n) * this._stretch() : 0
  }

  _stretch() {
    return this.hasStretchTarget ? Math.min(3, Math.max(1, Number(this.stretchTarget.value) || 1)) : 1
  }

  setStretch() {
    if (this.hasStretchValueTarget) this.stretchValueTarget.textContent = `×${this._stretch()}`
    if (!this.geometry) return
    this._raise()
    this.view.target.z = this._meanZ()
    this._render()
  }

  showPhoto() {
    this._swap("photo")
  }

  showHeights() {
    this._swap("heights")
  }

  async _swap(kind) {
    this._pressed(kind)
    if (!this.material) return
    const session = this.session
    if (!this.textures[kind]) {
      try {
        this.textures[kind] = await this._loadTexture(this.urls[kind])
      } catch {
        return
      }
      if (session !== this.session) return
    }
    this.material.map = this._sharp(this.textures[kind])
    this.material.needsUpdate = true
    this._render()
  }

  _pressed(kind) {
    for (const [name, button] of [["photo", this.photoButtonTarget], ["heights", this.heightsButtonTarget]]) {
      const on = name === kind
      button.setAttribute("aria-pressed", String(on))
      button.classList.toggle("bg-brand", on)
      button.classList.toggle("text-white", on)
      button.classList.toggle("bg-white", !on)
      button.classList.toggle("text-gray-700", !on)
      button.classList.toggle("hover:bg-gray-50", !on) // else the hover colour blanks the pressed button under the mouse
    }
  }

  // ── Camera: spin, tilt, zoom, move ───────────────────────────────────

  _place() {
    const { theta, phi, radius, target } = this.view
    this.camera.position.set(
      target.x + radius * Math.sin(phi) * Math.sin(theta),
      target.y - radius * Math.sin(phi) * Math.cos(theta),
      target.z + radius * Math.cos(phi)
    )
    this.camera.lookAt(target)
  }

  _render() {
    if (!this.renderer || !this.scene || !this.view || this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      if (!this.scene) return
      this._place()
      this.renderer.render(this.scene, this.camera)
    })
  }

  _resize() {
    if (!this.renderer || !this.camera) return
    const w = this.stageTarget.clientWidth
    const h = this.stageTarget.clientHeight
    if (!w || !h) return
    this.renderer.setSize(w, h, false)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this._render()
  }

  _listen() {
    const canvas = this.canvasTarget
    this.pointers = new Map()
    canvas.addEventListener("contextmenu", (event) => event.preventDefault())
    canvas.addEventListener("pointerdown", (event) => {
      canvas.setPointerCapture(event.pointerId)
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, button: event.button, shift: event.shiftKey })
      this.pinch = this.pointers.size === 2 ? this._pinch() : null
      canvas.classList.add("cursor-grabbing")
    })
    const end = (event) => {
      this.pointers.delete(event.pointerId)
      this.pinch = null
      if (!this.pointers.size) canvas.classList.remove("cursor-grabbing")
    }
    canvas.addEventListener("pointerup", end)
    canvas.addEventListener("pointercancel", end)
    canvas.addEventListener("pointerleave", () => this.readoutTarget.classList.add("hidden"))
    canvas.addEventListener("pointermove", (event) => {
      const p = this.pointers.get(event.pointerId)
      if (!p) return this._hover(event)
      if (this.pointers.size === 2) {
        p.x = event.clientX
        p.y = event.clientY
        const now = this._pinch()
        if (this.pinch) {
          this._zoom(this.pinch.d / Math.max(now.d, 1))
          this._pan(now.x - this.pinch.x, now.y - this.pinch.y)
        }
        this.pinch = now
        return
      }
      const dx = event.clientX - p.x
      const dy = event.clientY - p.y
      p.x = event.clientX
      p.y = event.clientY
      if (p.button === 2 || p.button === 1 || p.shift) this._pan(dx, dy)
      else this._orbit(dx, dy)
    })
    canvas.addEventListener("wheel", (event) => {
      event.preventDefault()
      this._zoom(Math.exp(event.deltaY * 0.0015))
    }, { passive: false })
  }

  _pinch() {
    const [a, b] = [...this.pointers.values()]
    return { d: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
  }

  _orbit(dx, dy) {
    if (!this.view) return
    this.view.theta -= dx * 0.006
    this.view.phi = Math.min(TILT_MAX, Math.max(TILT_MIN, this.view.phi - dy * 0.006))
    this._render()
  }

  _zoom(factor) {
    if (!this.view) return
    const size = Math.max(this.widthM, this.heightM)
    this.view.radius = Math.min(size * 6, Math.max(size * 0.03, this.view.radius * factor))
    this._render()
  }

  // Slide the point looked at along the ground, by how far the pointer went.
  _pan(dx, dy) {
    if (!this.view) return
    const h = this.stageTarget.clientHeight || 1
    const perPx = (2 * this.view.radius * Math.tan((this.camera.fov * Math.PI) / 360)) / h
    const right = new THREE.Vector3(Math.cos(this.view.theta), Math.sin(this.view.theta), 0)
    const ahead = new THREE.Vector3(-Math.sin(this.view.theta), Math.cos(this.view.theta), 0)
    this.view.target.addScaledVector(right, -dx * perPx).addScaledVector(ahead, dy * perPx)
    this._render()
  }

  // The ground's height under the pointer: where its ray meets the ground, found in a few passes.
  _hover(event) {
    if (!this.view || !this.grid) return
    const rect = this.canvasTarget.getBoundingClientRect()
    const ndc = new THREE.Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1)
    const ray = new THREE.Raycaster()
    ray.setFromCamera(ndc, this.camera)
    let z = this.view.target.z
    let hit = null
    for (let pass = 0; pass < 4; pass++) {
      const point = ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 0, 1), -z), new THREE.Vector3())
      if (!point) {
        hit = null
        break
      }
      hit = this._at(point.x, point.y)
      if (!hit) break
      z = hit.scaled
    }
    if (hit) {
      const m = this.lo + hit.z
      this.readoutTarget.textContent = `${m.toFixed(1)} m · ${Math.round(m * FT).toLocaleString()} ft`
    } else {
      this.readoutTarget.textContent = this.textValue.no_ground
    }
    this.readoutTarget.classList.remove("hidden")
  }

  _at(x, y) {
    const { cols, rows, z, ground } = this.grid
    const c = Math.round(((x + this.widthM / 2) / this.widthM) * (cols - 1))
    const r = Math.round(((this.heightM / 2 - y) / this.heightM) * (rows - 1))
    if (c < 0 || r < 0 || c >= cols || r >= rows || !ground[r * cols + c]) return null
    const h = z[r * cols + c]
    return { z: h, scaled: h * this._stretch() }
  }

  _note(text) {
    this.noteTarget.textContent = text || ""
    this.noteTarget.classList.toggle("hidden", !text)
  }

  _dispose() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    this.geometry?.dispose()
    this.material?.dispose()
    for (const texture of Object.values(this.textures || {})) texture?.dispose()
    this.geometry = this.material = this.mesh = this.scene = this.camera = this.view = this.grid = null
    this.textures = {}
    this.renderer?.clear()
  }
}
